
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { settingsdata } from "@/api/settings";
import KTFolder from "@/components/files/Folder.vue";
import KTFile from "@/components/files/File.vue";
import {
  getTeamMaterials,
  createTeamMaterialFolder,
  addTeamMaterialLink,
  renameTeamMaterial,
  deleteTeamMaterial,
  uploadTeamMaterial,
  TEAM_MATERIAL_ACCEPT,
  formatStorageBytes,
  fileIconType
} from "@/api/teamMaterials.api.js";

export default defineComponent({
  name: "team-materials",
  components: {
    KTFolder,
    KTFile
  },
  data() {
    return {
      loading: true,
      items: [] as any[],
      teamId: 0,
      currentParentId: 0,
      breadcrumbs: [] as { id: number; name: string }[],
      storageUsedBytes: 0,
      storageLimitBytes: 1073741824,
      canManageTeamMaterials: false,
      canViewTeamMaterials: false,
      isTeamLeader: false,
      hasAvailableUplineTeams: false,
      showNewFolderModal: false,
      showAddLinkModal: false,
      newFolderName: "",
      linkName: "",
      linkUrl: "",
      TEAM_MATERIAL_ACCEPT
    };
  },
  computed: {
    storagePercent(): number {
      if (!this.storageLimitBytes) return 0;
      return Math.min(100, Math.round((this.storageUsedBytes / this.storageLimitBytes) * 100));
    }
  },
  async mounted() {
    await this.loadSettings();
    if (this.canViewTeamMaterials) {
      await this.loadMaterials();
    }
    this.loading = false;
  },
  methods: {
    formatStorageBytes,
    fileIconType,
    async loadSettings() {
      const response = await settingsdata();
      const data = response.data || {};
      this.isTeamLeader = Boolean(data.isTeamLeader);
      this.hasAvailableUplineTeams = Boolean(data.hasAvailableUplineTeams);
      this.canManageTeamMaterials = Boolean(data.canManageTeamMaterials);
      this.canViewTeamMaterials = Boolean(data.canViewTeamMaterials);
      this.teamId = Number(data.teamMaterialTeamId || 0);
      this.storageUsedBytes = Number(data.teamMaterialStorageUsedBytes || 0);
      this.storageLimitBytes = Number(data.teamMaterialStorageLimitBytes || 1073741824);
    },
    async loadMaterials() {
      const response = await getTeamMaterials(this.teamId, this.currentParentId);
      if (response.data.result !== "SUCCESS") {
        Swal.fire({
          title: "",
          text: response.data.message || "Unable to load materials.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return;
      }
      this.items = response.data.items || [];
      this.storageUsedBytes = Number(response.data.storageUsedBytes || 0);
      this.storageLimitBytes = Number(response.data.storageLimitBytes || this.storageLimitBytes);
      this.canManageTeamMaterials = Boolean(response.data.canManage);
    },
    async openFolder(folderId: number, folderName?: string) {
      if (folderId > 0) {
        const existingIndex = this.breadcrumbs.findIndex((b) => b.id === folderId);
        if (existingIndex >= 0) {
          this.breadcrumbs = this.breadcrumbs.slice(0, existingIndex + 1);
        } else if (folderName) {
          this.breadcrumbs.push({ id: folderId, name: folderName });
        }
      } else {
        this.breadcrumbs = [];
      }
      this.currentParentId = folderId;
      this.loading = true;
      await this.loadMaterials();
      this.loading = false;
    },
    openItem(item: any) {
      if (!item.url) return;
      if (item.itemType === "link" || item.fileCategory === "image" || item.fileCategory === "pdf" || item.fileCategory === "video") {
        window.open(item.url, "_blank");
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = item.url;
      anchor.target = "_blank";
      anchor.download = item.name || "download";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    async createFolder() {
      if (!this.newFolderName.trim()) {
        Swal.fire({ title: "", text: "Folder name is required.", icon: "error", confirmButtonClass: "btn btn-secondary" });
        return;
      }
      const response = await createTeamMaterialFolder(this.teamId, this.currentParentId, this.newFolderName.trim());
      if (response.data.result === "SUCCESS") {
        this.showNewFolderModal = false;
        this.newFolderName = "";
        await this.loadMaterials();
        Swal.fire({ title: "", text: response.data.message, icon: "success", confirmButtonClass: "btn btn-secondary" });
      } else {
        Swal.fire({ title: "", text: response.data.message, icon: "error", confirmButtonClass: "btn btn-secondary" });
      }
    },
    async addLink() {
      if (!this.linkName.trim() || !this.linkUrl.trim()) {
        Swal.fire({ title: "", text: "Name and URL are required.", icon: "error", confirmButtonClass: "btn btn-secondary" });
        return;
      }
      const response = await addTeamMaterialLink(this.teamId, this.currentParentId, this.linkName.trim(), this.linkUrl.trim());
      if (response.data.result === "SUCCESS") {
        this.showAddLinkModal = false;
        this.linkName = "";
        this.linkUrl = "";
        await this.loadMaterials();
        Swal.fire({ title: "", text: response.data.message, icon: "success", confirmButtonClass: "btn btn-secondary" });
      } else {
        Swal.fire({ title: "", text: response.data.message, icon: "error", confirmButtonClass: "btn btn-secondary" });
      }
    },
    async handleFileUpload(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file) return;

      try {
        const response = await uploadTeamMaterial(this.teamId, this.currentParentId, file);
        if (response.data.success) {
          this.storageUsedBytes = Number(response.data.storageUsedBytes || this.storageUsedBytes);
          await this.loadMaterials();
          Swal.fire({ title: "", text: response.data.message || "File uploaded.", icon: "success", confirmButtonClass: "btn btn-secondary" });
        } else {
          Swal.fire({ title: "", text: response.data.message || "Upload failed.", icon: "error", confirmButtonClass: "btn btn-secondary" });
        }
      } catch (e) {
        Swal.fire({ title: "", text: e?.response?.data?.message || "Upload failed.", icon: "error", confirmButtonClass: "btn btn-secondary" });
      }
    },
    async promptRename(item: any) {
      const result = await Swal.fire({
        title: "Rename",
        input: "text",
        inputValue: item.name,
        showCancelButton: true,
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-light"
      });
      if (!result.isConfirmed || !result.value) return;

      const response = await renameTeamMaterial(item.id, String(result.value).trim());
      if (response.data.result === "SUCCESS") {
        await this.loadMaterials();
        Swal.fire({ title: "", text: response.data.message, icon: "success", confirmButtonClass: "btn btn-secondary" });
      } else {
        Swal.fire({ title: "", text: response.data.message, icon: "error", confirmButtonClass: "btn btn-secondary" });
      }
    },
    async confirmDelete(item: any) {
      const result = await Swal.fire({
        title: "Delete item?",
        text: `Delete "${item.name}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-light"
      });
      if (!result.isConfirmed) return;

      const response = await deleteTeamMaterial(item.id);
      if (response.data.result === "SUCCESS") {
        await this.loadMaterials();
        Swal.fire({ title: "", text: response.data.message, icon: "success", confirmButtonClass: "btn btn-secondary" });
      } else {
        Swal.fire({ title: "", text: response.data.message, icon: "error", confirmButtonClass: "btn btn-secondary" });
      }
    },
    formatDate(value: any) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString();
    },
    isPublishableItem(item: any) {
      if (!item?.url) return false;
      return item.itemType === "link"
        || item.fileCategory === "image"
        || item.fileCategory === "video"
        || item.fileCategory === "pdf";
    },
    publishItem(item: any) {
      this.$router.push({
        path: "/settings/ai247-portal",
        query: {
          tab: "publish",
          name: item.name || "",
          caption: item.name || "",
          mediaUrl: item.fileCategory === "link" ? "" : (item.url || ""),
          linkUrl: item.itemType === "link" ? (item.url || "") : ""
        }
      });
    }
  }
});
